import gql from 'graphql-tag';
import { client } from '../client';
import { ENTITY } from '../entities';
import { entity2StringFragment } from '../toString';
import {
	generateAsyncQuery,
	generatePaginatedSelectQuery,
	AsyncQuery,
} from '../graphql';
import { DataVizSelectQuery } from '../../graphql/query/DataVizSelectQuery';

/**
 * Configurazione e helper per la gestione delle Relationships360.
 *
 * Le Relationships360 sono una tabella polimorfica e DIREZIONALE: ogni record collega un
 * "from" ad un "to". Noi vogliamo gestire relazioni bidirezionali, quindi ad ogni
 * collegamento creiamo/eliminiamo SEMPRE entrambe le direzioni (A→B e B→A).
 *
 * Per ogni tipo di record 360 (sorgente) definiamo le "sezioni" di relazione, cioè i tipi
 * di record con cui può essere collegato. Ogni sezione corrisponde:
 *  - al campo di relazione persistito esposto sul tipo (es. Company360.Good360s)
 *  - eventualmente ad un campo di suggerimenti calcolato live dal backend
 *    (es. Company360.suggestedGood360s)
 */

export interface RelationSection {
	// nome del campo di relazione persistito sul tipo sorgente (es. 'Good360s')
	field: string;
	// tipo di entità collegata
	targetEntity: ENTITY;
	// chiave i18n del titolo della sezione
	labelKey: string;
	// eventuale campo di suggerimenti esposto sul tipo sorgente (es. 'suggestedGood360s')
	suggestionField?: string;
	// variabili per la ricerca nel picker; se assente si elencano le entità senza filtro
	searchVariables?: (text: string) => any;
	// nome del tipo di input filtro per la ricerca (default: `${targetEntity}FilterInput`).
	// Es. CompanyCertification usa `SearchByNameInput`.
	filterTypeName?: string;
	// se true nasconde il picker di ricerca: la sezione si cura solo dai suggerimenti
	// (es. Fiere: non si possono cercare globalmente le FairParticipation)
	suggestionsOnly?: boolean;
	// abilita la creazione di un NUOVO record collegabile direttamente dalla sezione
	// (es. crea una FairParticipation / CompanyCertification / GoodClaim e la collega)
	createKind?: 'fairParticipation' | 'companyCertification' | 'goodClaim';
	// abilita il riordino dei collegati (drag): l'ordine viene salvato tramite orderIndex.
	orderable?: boolean;
	// slot "keyed": la sezione gestisce UN SOLO collegamento, memorizzato come
	// Relationships360 con questa relation_key (e non come colonna del record sorgente).
	// Il campo `field` e' il campo GraphQL di sola lettura che espone lo slot.
	relationKey?: string;
	// tipologie del record sorgente per cui lo slot ha senso (vuoto/assente = tutte).
	// Per Trend360 coincide con i valori di trendType.
	forSourceTypes?: ReadonlyArray<string>;
	// i suggerimenti arrivano senza un ordine: li ordino per nome lato client
	sortSuggestions?: boolean;
}

export const trendNameSearch = (text: string) => ({
	filter: { trendName: text },
});
export const nameSearch = (text: string) => ({ filter: { name: text } });
// usata (fra gli altri) per le ADV, che non hanno `name`
export const titleSearch = (text: string) => ({ filter: { title: text } });

export const RELATION_SECTIONS: { [key: string]: RelationSection[] } = {
	[ENTITY.COMPANY_360]: [
		{
			field: 'Relation360_Good360s',
			targetEntity: ENTITY.GOOD_360,
			labelKey: 'Company360/relations/Good360s',
			suggestionField: 'suggestedGood360s',
		},
		{
			field: 'Relation360_Brands',
			targetEntity: ENTITY.BRAND,
			labelKey: 'Company360/relations/Brands',
			suggestionField: 'suggestedBrands',
			searchVariables: nameSearch,
		},
		{
			field: 'Relation360_Trend360s',
			targetEntity: ENTITY.TREND_360,
			labelKey: 'Company360/relations/Trend360s',
			searchVariables: trendNameSearch,
		},
		{
			// Fiere: si curano dai suggerimenti (le partecipazioni esistenti dell'azienda);
			// non c'è ricerca globale di FairParticipation.
			field: 'Relation360_FairParticipations',
			targetEntity: ENTITY.FAIR_PARTICIPATION,
			labelKey: 'Company360/relations/FairParticipations',
			suggestionField: 'suggestedFairParticipations',
			suggestionsOnly: true,
			createKind: 'fairParticipation',
		},
		{
			// Certificazioni: ci si cura dai suggerimenti (le certificazioni dell'azienda).
			// "Crea collegamento" associa all'azienda una CompanyCertification esistente
			// (companyPatch su companyCertifications) e poi i suggerimenti si aggiornano.
			field: 'Relation360_CompanyCertifications',
			targetEntity: ENTITY.COMPANY_CERTIFICATION,
			labelKey: 'Company360/relations/CompanyCertifications',
			suggestionField: 'suggestedCompanyCertifications',
			suggestionsOnly: true,
			createKind: 'companyCertification',
		},
		{
			// ADV: relazione normale come le altre. A differenza di Good360 (dove il pool sono le
			// ADV dell'azienda del prodotto) qui non ci sono suggerimenti: si cercano per titolo.
			field: 'Relation360_Advs',
			targetEntity: ENTITY.ADV,
			labelKey: 'Company360/relations/Advs',
			searchVariables: titleSearch,
			orderable: true,
		},
		{
			// Aziende simili: i suggerimenti sono le aziende con almeno una sezione editoriale
			// in comune (possono essere centinaia). Non orderable: il riordino ricrea anche
			// i link B→A, perdendo l'ordine che l'altra azienda ha dato ai propri simili.
			field: 'Relation360_Company360s',
			targetEntity: ENTITY.COMPANY_360,
			labelKey: 'Company360/relations/Company360s',
			suggestionField: 'suggestedSimilarCompany360s',
			sortSuggestions: true,
		},
	],
	[ENTITY.GOOD_360]: [
		{
			field: 'Relation360_Company360s',
			targetEntity: ENTITY.COMPANY_360,
			labelKey: 'Good360/relations/Company360s',
			suggestionField: 'suggestedCompany360s',
		},
		{
			field: 'Relation360_Trend360s',
			targetEntity: ENTITY.TREND_360,
			labelKey: 'Good360/relations/Trend360s',
			searchVariables: trendNameSearch,
			orderable: true,
		},
		{
			field: 'Relation360_EditorialSections',
			targetEntity: ENTITY.EDITORIAL_SECTION,
			labelKey: 'Good360/relations/EditorialSections',
			suggestionField: 'suggestedEditorialSections',
			searchVariables: nameSearch,
			orderable: true,
		},
		{
			// Claim: si cercano/creano GoodClaim (globali) e si collegano.
			field: 'Relation360_GoodClaims',
			targetEntity: ENTITY.GOOD_CLAIM,
			labelKey: 'Good360/relations/GoodClaims',
			searchVariables: nameSearch,
			createKind: 'goodClaim',
			orderable: true,
		},
		{
			// ADV: relazione normale come su Company360 (ricerca per titolo); in più qui c'è un
			// pool di suggerimenti, cioè le ADV dell'azienda del prodotto.
			field: 'Relation360_Advs',
			targetEntity: ENTITY.ADV,
			labelKey: 'Good360/relations/Advs',
			suggestionField: 'suggestedAdvs',
			searchVariables: titleSearch,
			orderable: true,
		},
	],
	[ENTITY.TREND_360]: [
		// slot singoli (Relationships360 con relation_key): sostituiscono le vecchie
		// colonne FK di Trend360, cosi' aggiungerne o toglierne uno non tocca lo schema
		{
			field: 'parentMacrotrend',
			targetEntity: ENTITY.TREND_360,
			labelKey: 'Trend360/PARENT_MACROTREND/label',
			relationKey: 'PARENT_MACROTREND',
			searchVariables: (text: string) => ({
				filter: { trendName: text, trendTypes: ['MACROTREND'] },
			}),
			forSourceTypes: ['POTENTIAL_TREND', 'CONFIRMED_TREND'],
		},
		{
			field: 'antithesisTrend',
			targetEntity: ENTITY.TREND_360,
			labelKey: 'Trend360/ANTITHESIS_TREND/label',
			relationKey: 'ANTITHESIS_TREND',
			searchVariables: trendNameSearch,
			forSourceTypes: ['CONFIRMED_TREND'],
		},
		{
			field: 'scoreEvolutionChart',
			targetEntity: ENTITY.DATA_VIZ,
			labelKey: 'Trend360/MACROTREND_SCORE_EVOLUTION/label',
			relationKey: 'MACROTREND_SCORE_EVOLUTION',
			forSourceTypes: ['MACROTREND'],
			// solo i DataViz riservati a Trend360 (stessa restrizione del form, vedi
			// dataVizAsyncQuery in utils/entities/trend360.tsx)
			searchVariables: () => ({ filter: { scope: 'TREND_360' } }),
		},
		{
			field: 'marketPulseSurvey',
			targetEntity: ENTITY.DATA_VIZ,
			labelKey: 'Trend360/MARKET_PULSE/label',
			relationKey: 'MARKET_PULSE',
			forSourceTypes: ['POTENTIAL_TREND', 'CONFIRMED_TREND'],
			searchVariables: () => ({ filter: { scope: 'TREND_360' } }),
		},
		{
			field: 'communitySurvey',
			targetEntity: ENTITY.DATA_VIZ,
			labelKey: 'Trend360/CONFIRMED_COMMUNITY_SURVEY/label',
			relationKey: 'CONFIRMED_COMMUNITY_SURVEY',
			forSourceTypes: ['CONFIRMED_TREND'],
			searchVariables: () => ({ filter: { scope: 'TREND_360' } }),
		},
		// IMPORTANT - le sezioni generiche qui sotto rispecchiano RELATIONS_BY_TYPE in
		// utils/entities/trend360.tsx (il form di create/edit): stessa logica di
		// "questo tipo di trend puo' avere solo queste relazioni", duplicata qui perche'
		// questo file e quello del form non condividono un'unica fonte. Tenerle in sync
		// se RELATIONS_BY_TYPE cambia. Nessuna sezione generica per DataViz: nessun
		// trendType ne espone una nel form, sono collegabili solo dai tre slot sopra.
		{
			field: 'Relation360_Company360s',
			targetEntity: ENTITY.COMPANY_360,
			labelKey: 'Trend360/relations/Company360s',
			forSourceTypes: ['CONFIRMED_TREND'],
		},
		{
			field: 'Relation360_Good360s',
			targetEntity: ENTITY.GOOD_360,
			labelKey: 'Trend360/relations/Good360s',
			forSourceTypes: ['POTENTIAL_TREND', 'CONFIRMED_TREND'],
		},
		{
			field: 'Relation360_EditorialSections',
			targetEntity: ENTITY.EDITORIAL_SECTION,
			labelKey: 'Trend360/relations/EditorialSections',
			searchVariables: nameSearch,
			forSourceTypes: ['CONFIRMED_TREND'],
		},
		{
			field: 'Relation360_Trend360s',
			targetEntity: ENTITY.TREND_360,
			labelKey: 'Trend360/relations/Trend360s',
			searchVariables: trendNameSearch,
			forSourceTypes: ['POTENTIAL_TREND'],
		},
		{
			field: 'Relation360_Magazines',
			targetEntity: ENTITY.MAGAZINE,
			labelKey: 'Trend360/relations/Magazines',
			searchVariables: nameSearch,
			forSourceTypes: ['CONFIRMED_TREND'],
		},
		{
			field: 'Relation360_NewsletterIssues',
			targetEntity: ENTITY.NEWSLETTER_ISSUE,
			labelKey: 'Trend360/relations/NewsletterIssues',
			searchVariables: nameSearch,
			forSourceTypes: ['CONFIRMED_TREND'],
		},
		{
			field: 'Relation360_Advs',
			targetEntity: ENTITY.ADV,
			labelKey: 'Trend360/relations/Advs',
			searchVariables: titleSearch,
			forSourceTypes: ['CONFIRMED_TREND'],
		},
		{
			field: 'Relation360_Awards',
			targetEntity: ENTITY.AWARD,
			labelKey: 'Trend360/relations/Awards',
			searchVariables: nameSearch,
			forSourceTypes: ['CONFIRMED_TREND'],
		},
	],
};

export const getRelationSections = (entityName: ENTITY): RelationSection[] =>
	RELATION_SECTIONS[entityName] || [];

/**
 * Query che scarica tutte le relazioni persistite del record sorgente, una selezione per
 * sezione. I campi di relazione restituiscono liste semplici (non connection).
 */
export const buildRelationsQuery = (entityName: ENTITY) => {
	const sections = getRelationSections(entityName);
	const selections = sections
		.map(
			(s) => `
			${s.field} {
				id
				localId
				${entity2StringFragment(s.targetEntity)}
			}`,
		)
		.join('\n');

	return gql`
		query Relations360Query($id: ID!) {
			entity: node(id: $id) {
				id
				... on ${entityName} {
					${selections}
				}
			}
		}
	`;
};

/**
 * Query (lazy) che scarica i suggerimenti per una singola sezione. Calcolati live dal
 * backend, non sono link persistiti.
 */
export const buildSuggestionsQuery = (
	entityName: ENTITY,
	section: RelationSection,
) => gql`
	query Suggestions360Query($id: ID!) {
		entity: node(id: $id) {
			id
			... on ${entityName} {
				${section.suggestionField} {
					id
					localId
					${entity2StringFragment(section.targetEntity)}
				}
			}
		}
	}
`;

export const makeSearchQuery = (section: RelationSection): AsyncQuery =>
	// IMPORTANT - DataViz e' un'eccezione: la query generica sotto produrrebbe il campo
	// "dataVizs" (plurale), che non esiste nello schema (il campo si chiama "DataViz",
	// maiuscolo e singolare - vedi il commento in DataVizSelectQuery.ts). Si riusa qui
	// la stessa query gia' corretta che usa il form di Trend360, invece di generarne
	// una rotta.
	section.targetEntity === ENTITY.DATA_VIZ
		? generateAsyncQuery(
				DataVizSelectQuery,
				true,
				section.searchVariables || (() => ({ filter: {} })),
		  )
		: generateAsyncQuery(
				// order=false: company360s/good360s/trend360s non supportano orderBy:name
				generatePaginatedSelectQuery(
					section.targetEntity,
					false,
					section.filterTypeName ? () => section.filterTypeName as string : undefined,
				),
				true,
				section.searchVariables || (() => ({ filter: {} })),
		  );

/**
 * Query dei record Relationships360 (join) che coinvolgono il record sorgente, in entrambe
 * le direzioni. Serve per ottenere i global id dei join da cancellare: il bulk delete lavora
 * sugli id dei record Relationships360, non sugli id delle entità collegate.
 */
export const RELATIONS_LINKS_QUERY = gql`
	query Relationships360Links($id: ID!) {
		fromLinks: relationships360s(filter: { from: { id: $id } }, first: 1000) {
			edges {
				node {
					id
					toRecordType
					toRecordId
					relationKey
				}
			}
		}
		toLinks: relationships360s(filter: { to: { id: $id } }, first: 1000) {
			edges {
				node {
					id
					fromRecordType
					fromRecordId
					relationKey
				}
			}
		}
	}
`;

export interface RelationLinks {
	from: Array<{
		id: string;
		toRecordType: string;
		toRecordId: number;
		relationKey?: string | null;
	}>;
	to: Array<{
		id: string;
		fromRecordType: string;
		fromRecordId: number;
		relationKey?: string | null;
	}>;
}

export const emptyLinks = (): RelationLinks => ({ from: [], to: [] });

export const parseLinks = (data: any): RelationLinks => ({
	from: ((data && data.fromLinks && data.fromLinks.edges) || []).map(
		(e: any) => e.node,
	),
	to: ((data && data.toLinks && data.toLinks.edges) || []).map(
		(e: any) => e.node,
	),
});

/**
 * Restituisce i global id dei record Relationships360 che collegano la sorgente all'entità
 * (di tipo `targetType` e local id `targetLocalId`), in entrambe le direzioni. I tipi 360
 * coincidono con il nome del GraphQL type (== valore dell'enum ENTITY), quindi il confronto
 * con fromRecordType/toRecordType è diretto.
 */
export const findJoinIds = (
	links: RelationLinks,
	targetType: string,
	targetLocalId: number,
): string[] => {
	const ids: string[] = [];
	// IMPORTANT - le relazioni "keyed" sono gestite dal campo dedicato del record
	// sorgente: non devono essere toccate da chi scollega dalle sezioni generiche
	links.from.forEach((n) => {
		if (
			!n.relationKey &&
			n.toRecordType === targetType &&
			n.toRecordId === targetLocalId
		) {
			ids.push(n.id);
		}
	});
	links.to.forEach((n) => {
		if (
			!n.relationKey &&
			n.fromRecordType === targetType &&
			n.fromRecordId === targetLocalId
		) {
			ids.push(n.id);
		}
	});
	return ids;
};

/**
 * Restituisce l'id del record Relationships360 che occupa lo slot `relationKey`
 * (le relazioni keyed sono scritte in una sola direzione: from = record sorgente).
 */
export const findKeyedJoinIds = (
	links: RelationLinks,
	relationKey: string,
): string[] =>
	links.from.filter((n) => n.relationKey === relationKey).map((n) => n.id);

const BULK_MUTATION = gql`
	mutation Relationships360Bulk($input: Relationships360BulkInput!) {
		relationships360Bulk(input: $input) {
			deletedCount
			created {
				id
			}
		}
	}
`;

export interface RefPair {
	from: { id: string };
	to: { id: string };
	orderIndex?: number;
	// slot semantico: la relazione e' gestita dal campo dedicato del record sorgente
	// ed e' unica per (from, relationKey) - crearne una nuova sostituisce la precedente
	relationKey?: string;
}

/**
 * Applica in UN'UNICA richiesta i delete e i create accumulati. I delete (global id dei
 * record Relationships360) vengono eseguiti prima dei create, in un'unica transazione lato BE.
 */
export const applyRelationships360Bulk = async (
	deleteIds: string[],
	creates: RefPair[],
): Promise<void> => {
	if (!deleteIds.length && !creates.length) {
		return;
	}
	await client.mutate({
		mutation: BULK_MUTATION,
		variables: { input: { delete: deleteIds, create: creates } },
	});
};

/**
 * Costruisce il create di una relazione "keyed": UNA sola direzione (from = record che
 * possiede lo slot), perché il verso opposto si ricava interrogando questa.
 * È unica per (from, relationKey): crearne una nuova sostituisce la precedente.
 */
export const keyedCreatePair = (
	fromId: string,
	toId: string,
	relationKey: string,
): RefPair => ({
	from: { id: fromId },
	to: { id: toId },
	relationKey,
});

/**
 * Costruisce le coppie di create per un collegamento bidirezionale (A→B e B→A).
 * `orderIndex` (se fornito) viene applicato alla direzione sorgente→target (A→B), che è quella
 * che alimenta i campi Relation360_* ordinati.
 */
export const bidirectionalCreatePairs = (
	aId: string,
	bId: string,
	orderIndex?: number,
): RefPair[] => [
	{
		from: { id: aId },
		to: { id: bId },
		...(orderIndex != null ? { orderIndex } : {}),
	},
	{ from: { id: bId }, to: { id: aId } },
];